import { useCallback, useEffect, useRef, useState } from 'react';

import {
  MonitoredCompany,
  ProposalCompanyPayload,
  ProposalCompanyResponse,
  confirmProposalCompany,
  getProposalCompany,
  listMonitoredCompanies,
  updateProposalCompany,
} from 'services/actions/ai_management_proposal_company';

interface Options {
  trackSave: <T>(request: Promise<T>) => Promise<T>;
}

const onlyDigits = (value: string) => value.replace(/\D/g, '');

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status/corpo.
const resultOrError = (res: {
  data: unknown;
  status?: number;
}): { body: ProposalCompanyResponse | null; error: string | null } => {
  const status = res?.status ?? 200;
  const body = res?.data as ProposalCompanyResponse & { error?: string };
  if (status >= 400 || body?.error) {
    return { body: null, error: body?.error || 'Não foi possível salvar.' };
  }
  return { body, error: null };
};

export function useProposalCompany(biddingId: number | null, { trackSave }: Options) {
  const [monitored, setMonitored] = useState<MonitoredCompany[]>([]);
  const [cnpj, setCnpj] = useState<string | null>(null);
  const [company, setCompany] = useState<ProposalCompanyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let active = true;
    listMonitoredCompanies()
      .then((res) => {
        if (!active) return;
        const lista = (res?.data?.empresas ?? [])
          .map((e) => ({
            cnpj: onlyDigits(e.monitored_company || e.cnpj || ''),
            nome: e.company_name ?? null,
          }))
          .filter((e) => e.cnpj !== '');
        setMonitored(lista);
        setCnpj((atual) => atual ?? lista[0]?.cnpj ?? null);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const load = useCallback(() => {
    if (!biddingId || !cnpj) return;
    const id = biddingId;
    setLoading(true);
    setError(false);
    getProposalCompany(id, cnpj)
      .then((res) => {
        if (biddingRef.current !== id) return;
        const { body } = resultOrError(res);
        if (body) setCompany(body);
        else setError(true);
      })
      .catch(() => {
        if (biddingRef.current === id) setError(true);
      })
      .finally(() => {
        if (biddingRef.current === id) setLoading(false);
      });
  }, [biddingId, cnpj]);

  useEffect(() => {
    load();
  }, [load]);

  const flashSaved = () => {
    setSavedFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSavedFlash(false), 4000);
  };

  // salvar_no_perfil true vale para as próximas propostas. O erro volta para a tela, sem expor
  // CPF/RG em toast.
  const save = useCallback(
    (payload: Omit<ProposalCompanyPayload, 'cnpj'>): Promise<string | null> => {
      if (!biddingId || !cnpj) return Promise.resolve('Selecione o CNPJ.');
      return trackSave(updateProposalCompany(biddingId, { ...payload, cnpj }))
        .then((res) => {
          const { body, error: err } = resultOrError(res);
          if (body) {
            setCompany(body);
            flashSaved();
            return null;
          }
          return err;
        })
        .catch(() => 'Não foi possível salvar os dados da empresa.');
    },
    [biddingId, cnpj, trackSave],
  );

  const confirm = useCallback(
    (confirmada: boolean) => {
      if (!biddingId || !cnpj) return;
      trackSave(confirmProposalCompany(biddingId, cnpj, confirmada))
        .then((res) => {
          const { body } = resultOrError(res);
          if (body) setCompany(body);
        })
        .catch(() => undefined);
    },
    [biddingId, cnpj, trackSave],
  );

  return {
    monitored,
    cnpj,
    setCnpj,
    company,
    loading,
    error,
    savedFlash,
    save,
    confirm,
    reload: load,
  };
}
